const LOGIN_USER_ID_REGEX = /^\d{1,49}$/;
const VISITOR_USER_ID_REGEX = /^[a-f0-9]{32}$/;
const SESSION_TIME_TOKEN_V2_REGEX = /^\d{14}$/;
const SESSION_TIME_TOKEN_V1_REGEX = /^\d{12}[a-z0-9]{2}$/;

export const SESSION_ID_MAX_LENGTH = 64;
export const SESSION_TIME_TOKEN_LENGTH = 14;

function getTimestampPrefix(date: Date): string {
  const year = String(date.getUTCFullYear()).padStart(4, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');

  return `${year}${month}${day}${hours}${minutes}${seconds}`;
}

export function normalizeUserId(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    if (LOGIN_USER_ID_REGEX.test(trimmed)) {
      return trimmed;
    }

    const normalizedHex = trimmed.toLowerCase();
    if (VISITOR_USER_ID_REGEX.test(normalizedHex)) {
      return normalizedHex;
    }

    return null;
  }

  if (typeof value === 'number') {
    if (!Number.isInteger(value) || value < 0) {
      return null;
    }

    return normalizeUserId(String(value));
  }

  if (typeof value === 'bigint') {
    if (value < BigInt(0)) {
      return null;
    }

    return normalizeUserId(value.toString());
  }

  return null;
}

export function isValidUserId(value: unknown): boolean {
  return normalizeUserId(value) !== null;
}

export function generateSessionId(date = new Date()): string {
  return getTimestampPrefix(date);
}

function normalizeSessionTimeTokenInternal(rawToken: string): string | null {
  const token = rawToken.toLowerCase();
  if (SESSION_TIME_TOKEN_V2_REGEX.test(token)) {
    const year = Number.parseInt(token.slice(0, 4), 10);
    if (!Number.isFinite(year) || year < 2000 || year > 2099) {
      return null;
    }
    return token;
  }

  if (SESSION_TIME_TOKEN_V1_REGEX.test(token)) {
    const yy = Number.parseInt(token.slice(0, 2), 10);
    const mm = Number.parseInt(token.slice(2, 4), 10);
    const dd = Number.parseInt(token.slice(4, 6), 10);
    const hh = Number.parseInt(token.slice(6, 8), 10);
    const mi = Number.parseInt(token.slice(8, 10), 10);
    const ss = Number.parseInt(token.slice(10, 12), 10);

    if (
      !Number.isFinite(yy) ||
      !Number.isFinite(mm) ||
      !Number.isFinite(dd) ||
      !Number.isFinite(hh) ||
      !Number.isFinite(mi) ||
      !Number.isFinite(ss)
    ) {
      return null;
    }

    if (yy < 0 || yy > 99) return null;
    if (mm < 1 || mm > 12) return null;
    if (dd < 1 || dd > 31) return null;
    if (hh < 0 || hh > 23) return null;
    if (mi < 0 || mi > 59) return null;
    if (ss < 0 || ss > 59) return null;

    return token;
  }

  return null;
}

export function normalizeSessionTimeToken(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const token = normalizeSessionTimeTokenInternal(trimmed);
  if (!token) return null;
  if (SESSION_TIME_TOKEN_V2_REGEX.test(token)) return token;
  return `20${token.slice(0, 12)}`;
}

export function buildSessionId(userId: unknown, date = new Date()): string | null {
  const normalizedUserId = normalizeUserId(userId);
  if (!normalizedUserId) {
    return null;
  }

  const sessionId = `${normalizedUserId}-${generateSessionId(date)}`;
  if (sessionId.length > SESSION_ID_MAX_LENGTH) {
    return null;
  }

  return sessionId;
}

export function parseSessionId(value: unknown): { userId: string; timeToken: string } | null {
  if (typeof value !== 'string') {
    return null;
  }

  const separatorIndex = value.lastIndexOf('-');
  if (separatorIndex <= 0 || separatorIndex === value.length - 1) {
    return null;
  }

  const userId = normalizeUserId(value.slice(0, separatorIndex));
  const rawTimeToken = value.slice(separatorIndex + 1);
  const timeToken = normalizeSessionTimeTokenInternal(rawTimeToken);

  if (!userId || !timeToken) {
    return null;
  }

  const normalizedSessionId = `${userId}-${timeToken}`;
  if (normalizedSessionId.length !== value.length || normalizedSessionId.length > SESSION_ID_MAX_LENGTH) {
    return null;
  }

  return {
    userId,
    timeToken,
  };
}

export function isSessionTimeToken(value: unknown): value is string {
  return normalizeSessionTimeToken(value) !== null;
}

export function isValidSessionId(value: unknown): boolean {
  return parseSessionId(value) !== null;
}

export function isSessionOwnedByUser(sessionId: unknown, userId: unknown): boolean {
  const parsedSessionId = parseSessionId(sessionId);
  const normalizedUserId = normalizeUserId(userId);

  return !!parsedSessionId && !!normalizedUserId && parsedSessionId.userId === normalizedUserId;
}
